/**
 * This file defines the invite() service.
 */

import OriginTracer from '@atlassiansox/origin-tracing';

import {
	TRACK_EVENT_TYPE,
	OPERATIONAL_EVENT_TYPE,
	type GasCorePayload,
	type GasPurePayload,
} from '@atlaskit/analytics-gas-types';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { AnyAri } from '@atlassian/ari/any-ari';

import type {
	AbstractServiceMarker,
	AnyServiceWithDeps,
	DependenciesContext,
} from '../../core/types';
import { toError } from '../../core/utils';
import {
	createAnalyticsEvent,
	EnablementTouchpoint,
	type GetAdditionalAttributes,
	getAnalyticsChannel,
	getCurrentCloudId,
	getCurrentProduct,
	getCurrentUserId,
	getErrorDetails,
	getStargateURL,
	isAcceptableFailure,
	isAcceptableFailureForUserInvite,
	type Product,
	shouldReportFailedInvite,
} from '../common/common';
import { fetchJSON, type FetchInput, type FetchResponse } from '../common/fetchJSON';
import { fireErrorAnalytics } from '../common/fireErrorAnalytics';

import { getContinueUrlWithOrigin } from './utils';

type User = {
	email?: string;
	id?: string;
};

type InviteSuccessResult = User & {
	integration?: string;
	origin?: OriginTracer;
	resource: AnyAri;
	touchpoint: EnablementTouchpoint;
	traceId?: string;
} & (
		| {
				error: null;
				inviteStatus: 'INVITED';
		  }
		| {
				error: null;
				inviteStatus: 'USER_EXISTS';
		  }
		| {
				error: null;
				inviteStatus: 'INVITED_PENDING_APPROVAL';
		  }
		| {
				error: null;
				inviteStatus: 'PENDING_INVITE_EXISTS';
		  }
		| {
				error: null;
				inviteStatus: 'NOT_INVITED';
		  }
		| {
				error: InviteErrorReason;
				inviteStatus: 'ERROR';
		  }
	);

type InviteFailedResult = {
	error: Error;
	errorReason: InviteErrorReason;
	// When entire request fails, for example, bad request, network error, etc.
	// TODO: This can be more specific, but we need to know what the response status is.
	inviteStatus: null;
	traceId?: string;
};

export type InviteResult = InviteSuccessResult | InviteFailedResult;

export const INVITE_STATUSES = [
	'INVITED',
	'USER_EXISTS',
	'INVITED_PENDING_APPROVAL',
	'PENDING_INVITE_EXISTS',
	'NOT_INVITED',
	'ERROR',
] as const;

export type InviteStatus = (typeof INVITE_STATUSES)[number];

type InviteErrorReason = {
	code: string;
	message: string;
	status?: number;
};

export type InviteResponse = (User & {
	errorReasons?: {
		[resource: string]: InviteErrorReason;
	};
	results: {
		[resource: string]: InviteStatus;
	};
})[];

export interface RecaptchaOptions {
	recaptchaAction?: string;
	recaptchaToken?: string;
}

export interface CastleOptions {
	castleRequestToken?: string;
}

// Input - data directly needed to invite a user/s to a product:
export type InviteInput = {
	castleOptions?: CastleOptions;
	/**
	 * The URL the invitee lands on when accepting the invite.
	 *
	 * If a continueUrl is not provided, the invitee will be redirected to the product landing page.
	 *
	 * NOTE: The continueUrl should not contain the Origin Tracing query parameter: atlOrigin=
	 * as this will be overridden by the service.
	 * @default undefined
	 */
	continueUrl?: string;
	getAdditionalAttributes?: GetAdditionalAttributes<InviteSuccessResult>;
	/**
	 * Called with the continueUrl the service generated (including Origin Tracing),
	 * letting the caller add or override query params before it's used.
	 *
	 * For example, Confluence's NthUserOnboarding flow (see getLandingPath) reads a
	 * &source= param off this URL to route the invitee to a source-specific landing
	 * page — callers whose invitees may land there should append it here.
	 * @default undefined
	 */
	getContinueUrl?: (continueUrl: string) => string;
	notes?: Record<string, string>;
	recaptchaOptions?: RecaptchaOptions;
	resources: AnyAri[];
	users: User[];
} & (
	| {
			integration?: string;
			touchpoint: Exclude<EnablementTouchpoint, 'unknown'>;
	  }
	| {
			integration: string;
			touchpoint: Extract<EnablementTouchpoint, 'unknown'>;
	  }
);

// Dependencies - generally common to all invites:
const dependencies: {
	createAnalyticsEvent: CreateUIAnalyticsEvent & AbstractServiceMarker;
	fetchJSON: (<Response>(input: FetchInput) => Promise<FetchResponse<Response>>) &
		AbstractServiceMarker;
	fireErrorAnalytics: typeof fireErrorAnalytics;
	getAnalyticsChannel: typeof getAnalyticsChannel;
	getCurrentCloudId: (() => string) & AbstractServiceMarker;
	getCurrentProduct: (() => Product) & AbstractServiceMarker;
	getCurrentUserId: (() => string) & AbstractServiceMarker;
	getStargateURL: (url: `/${string}`) => string;
} = {
	fetchJSON,
	createAnalyticsEvent,
	fireErrorAnalytics,
	getAnalyticsChannel,
	getStargateURL,
	getCurrentUserId,
	getCurrentCloudId,
	getCurrentProduct,
};

type Deps = typeof dependencies;

export const invite: typeof inviteImpl & {
	dependencies: {
		createAnalyticsEvent: CreateUIAnalyticsEvent & AbstractServiceMarker;
		fetchJSON: (<Response>(input: FetchInput) => Promise<FetchResponse<Response>>) &
			AbstractServiceMarker;
		fireErrorAnalytics: typeof fireErrorAnalytics;
		getAnalyticsChannel: typeof getAnalyticsChannel;
		getCurrentCloudId: (() => string) & AbstractServiceMarker;
		getCurrentProduct: (() => Product) & AbstractServiceMarker;
		getCurrentUserId: (() => string) & AbstractServiceMarker;
		getStargateURL: (url: `/${string}`) => string;
	};
} = Object.assign(inviteImpl, {
	dependencies,
}) satisfies AnyServiceWithDeps<Deps>;

async function inviteImpl(
	input: InviteInput,
	context: DependenciesContext<Deps>,
): Promise<InviteResult[]> {
	const deps = context.dependencies();
	let results: InviteResult[];
	const getContinueUrl = input.getContinueUrl ?? ((continueUrl: string): string => continueUrl);

	const usersWithOrigin = input.users.map((user) => {
		const origin = new OriginTracer({ product: deps.getCurrentProduct() });
		const continueUrl = getContinueUrl(
			getContinueUrlWithOrigin(input.continueUrl, input.resources, origin),
		);
		return {
			user: {
				...user,
				continueUrl,
			},
			origin,
		};
	});

	const users = usersWithOrigin.map(({ user }) => user);

	try {
		const fetchResponse = await deps.fetchJSON<InviteResponse>({
			url: deps.getStargateURL('/v3/invitations/invite'),
			method: 'POST',
			credentials: 'include',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
				...(input.recaptchaOptions?.recaptchaToken
					? { 'G-Recaptcha-Token': input.recaptchaOptions.recaptchaToken }
					: {}),
				...(input.recaptchaOptions?.recaptchaAction
					? { 'Recaptcha-Action': input.recaptchaOptions.recaptchaAction }
					: {}),
				...(input.castleOptions?.castleRequestToken
					? { 'X-Atl-Castle-Request-Token': input.castleOptions.castleRequestToken }
					: {}),
			},
			body: JSON.stringify({
				users,
				resources: input.resources.map((resource) => resource.toString()),
				metadata: {
					...(input.touchpoint === EnablementTouchpoint.UNKNOWN
						? {}
						: { touchpoint: input.touchpoint }),
					sourceProduct: deps.getCurrentProduct(),
					...(input.notes && { notes: input.notes }),
				},
			}),
		});
		results = mapInviteResponse(
			fetchResponse.data,
			usersWithOrigin,
			input.touchpoint,
			input.integration,
			fetchResponse.traceId,
		);
	} catch (error) {
		const errorReason = getErrorDetails(error);
		const { code, message, status } = errorReason;
		const attributes: ErrorAnalyticsAttributes = {
			code: code,
			message: message,
			status: status,
		};

		if (!isAcceptableFailure(message, status)) {
			// No targetProduct/targetTenantId/userAaid here: the whole request can span
			// multiple resources (products/tenants) and multiple users, so there's no
			// single target or invitee to attribute this failure to.
			deps.fireErrorAnalytics({
				error: new Error(message),
				meta: { id: 'sloInviteRequest' },
				attributes: {
					...(input.getAdditionalAttributes?.() ?? {}),
					...attributes,
					integration: input.integration,
					touchpoint: input.touchpoint,
					actorAaid: deps.getCurrentUserId(),
					sourceProduct: deps.getCurrentProduct(),
					sourceTenantId: deps.getCurrentCloudId(),
				},
			});
		}
		results = [{ inviteStatus: null, error: toError(error), errorReason }];
	}

	results.forEach((result) => fireInviteAnalytics(result, context, input.getAdditionalAttributes));
	return results;
}

export const mapInviteResponse = (
	response: InviteResponse,
	usersWithOrigin: { origin: OriginTracer; user: User }[],
	touchpoint: EnablementTouchpoint,
	integration?: string,
	traceId?: string,
): InviteResult[] =>
	response.flatMap((user) => {
		const origin = usersWithOrigin.find(
			(userWithOrigin) =>
				userWithOrigin.user.id === user.id || userWithOrigin.user.email === user.email,
		)?.origin;
		return Object.keys(user.results).map((resource) => {
			const inviteStatus = user.results[resource];
			const resourceAri = AnyAri.parse(resource);
			const common = {
				id: user.id,
				email: user.email,
				origin,
				integration,
				touchpoint,
				resource: resourceAri,
				traceId,
			};

			if (inviteStatus === 'ERROR') {
				return {
					...common,
					inviteStatus,
					// errorReasons should always be present for error responses
					error: user.errorReasons?.[resource] ?? {
						code: 'UNKNOWN_ERROR',
						message: 'Unknown error',
					},
				};
			}

			return {
				...common,
				inviteStatus,
				error: null,
			};
		});
	});

export type RequiredFields<T, K extends keyof T> = T & Required<Pick<T, K>>;

interface BulkInviteResult {
	error: RequiredFields<
		Extract<InviteResult, { inviteStatus: 'NOT_INVITED' | 'ERROR' }>,
		'email'
	>[];
	failure: Extract<InviteResult, { inviteStatus: null }> | undefined;
	invited: RequiredFields<
		Extract<InviteResult, { inviteStatus: 'INVITED' | 'USER_EXISTS' }>,
		'email' | 'id'
	>[];
	requested: RequiredFields<
		Extract<InviteResult, { inviteStatus: 'INVITED_PENDING_APPROVAL' | 'PENDING_INVITE_EXISTS' }>,
		'email' | 'id'
	>[];
}

const isInvited = (res: InviteResult): res is BulkInviteResult['invited'][number] => {
	return res.inviteStatus === 'INVITED' || res.inviteStatus === 'USER_EXISTS';
};

const isRequested = (res: InviteResult): res is BulkInviteResult['requested'][number] => {
	return (
		res.inviteStatus === 'INVITED_PENDING_APPROVAL' || res.inviteStatus === 'PENDING_INVITE_EXISTS'
	);
};

const isError = (res: InviteResult): res is BulkInviteResult['error'][number] => {
	return res.inviteStatus === 'NOT_INVITED' || res.inviteStatus === 'ERROR';
};

const bucketInviteResults = (result: InviteResult[]): BulkInviteResult =>
	result.reduce<BulkInviteResult>(
		(accumulator, res) => {
			switch (res.inviteStatus) {
				case 'INVITED':
				case 'USER_EXISTS':
					isInvited(res) && accumulator.invited.push(res);
					break;
				case 'INVITED_PENDING_APPROVAL':
				case 'PENDING_INVITE_EXISTS':
					isRequested(res) && accumulator.requested.push(res);
					break;
				case 'NOT_INVITED':
				case 'ERROR':
					isError(res) && accumulator.error.push(res);
					break;
				default:
					accumulator.failure = res;
			}
			return accumulator;
		},
		{
			invited: [],
			requested: [],
			error: [],
			failure: undefined,
		},
	);

export const mapInviteResult: (result: InviteResult[]) => BulkInviteResult = bucketInviteResults;

export type InviteUserResult = {
	email: string;
	id: string;
	results: { [resource: string]: InviteStatus };
};

export type InviteErrorResult = {
	email: string;
	error?: { code: string; message: string };
	id?: string;
};

export type GroupedBulkInviteResult = {
	error: InviteErrorResult[];
	failure: BulkInviteResult['failure'];
	invited: InviteUserResult[];
	requested: InviteUserResult[];
};

const toInviteErrorResult = (result: BulkInviteResult['error'][number]): InviteErrorResult => ({
	...(result.id ? { id: result.id } : {}),
	email: result.email,
	error: result.error ? { code: result.error.code, message: result.error.message } : undefined,
});

const groupInvitedOrRequestedByUser = (
	results: BulkInviteResult['invited'] | BulkInviteResult['requested'],
): InviteUserResult[] => {
	const usersById = results.reduce((acc, result) => {
		const existingUser = acc.get(result.id);
		if (existingUser) {
			existingUser.results[result.resource.toString()] = result.inviteStatus;
		} else {
			acc.set(result.id, {
				id: result.id,
				email: result.email,
				results: { [result.resource.toString()]: result.inviteStatus },
			});
		}
		return acc;
	}, new Map<string, InviteUserResult>());
	return Array.from(usersById.values());
};

const groupErrorsByUser = (results: BulkInviteResult['error']): InviteErrorResult[] => {
	const usersByKey = results.reduce((acc, result) => {
		const key = result.id ?? result.email;
		if (!acc.has(key)) {
			acc.set(key, toInviteErrorResult(result));
		}
		return acc;
	}, new Map<string, InviteErrorResult>());
	return Array.from(usersByKey.values());
};

export const mapInviteResultByUser = (result: InviteResult[]): GroupedBulkInviteResult => {
	const bucketed = bucketInviteResults(result);

	return {
		invited: groupInvitedOrRequestedByUser(bucketed.invited),
		requested: groupInvitedOrRequestedByUser(bucketed.requested),
		error: groupErrorsByUser(bucketed.error),
		failure: bucketed.failure,
	};
};

// Analytics functions for these common events might later become
// exported for standalone use
function fireInviteAnalytics(
	result: InviteResult,
	context: DependenciesContext<Deps>,
	getAdditionalAttributes?: GetAdditionalAttributes<InviteSuccessResult>,
) {
	if (result.inviteStatus === null) {
		// Whole-request failures are handled directly in inviteImpl's catch block.
		return;
	}

	const { integration, resource, touchpoint, traceId } = result;
	const baseAttributes = {
		...(getAdditionalAttributes?.(result) ?? {}),
		actorAaid: context.dependencies().getCurrentUserId(),
		integration,
		sourceProduct: context.dependencies().getCurrentProduct(),
		sourceTenantId: context.dependencies().getCurrentCloudId(),
		targetProduct: resource.resourceOwner as Product,
		targetTenantId: resource.resourceId,
		touchpoint,
		...(traceId ? { traceId } : {}),
	};

	if (!result.error) {
		const originAttributes =
			result.origin?.toAnalyticsAttributes({
				hasGeneratedId: true,
			}) ?? {};
		const attributes: AnalyticsAttributes = {
			...originAttributes,
			...baseAttributes,
			// The id is always present for success responses
			userAaid: result.id!,
			inviteStatus: result.inviteStatus,
		};

		inviteSucceededSLO(attributes, context);

		if (result.inviteStatus === 'INVITED') {
			fireUserInvited(attributes, context);
		}
		if (result.inviteStatus === 'INVITED_PENDING_APPROVAL') {
			fireProductAccessRequested(attributes, context);
		}
		return;
	}

	const failure = result.error;
	// There should not be a failure SLO event if the error is a user network failure error
	if (isAcceptableFailureForUserInvite(failure)) {
		return;
	}

	if (!shouldReportFailedInvite(failure)) {
		inviteSucceededSLO(
			{ ...baseAttributes, ...(result.id ? { userAaid: result.id } : {}) },
			context,
		);
		return;
	}

	context.dependencies().fireErrorAnalytics({
		error: new Error(failure.message),
		meta: { id: 'sloInviteRequest' },
		attributes: {
			...failure,
			...baseAttributes,
			...(result.id ? { userAaid: result.id } : {}),
		},
	});
}

type OriginAttributes = ReturnType<OriginTracer['toAnalyticsAttributes']>;

export type TrackEventAttributes = {
	actorAaid: string; // AAID of user initiating request (same as above if RA1/3, different if RA2)
	integration?: string; // Integration being used to invite the user
	inviteStatus: InviteStatus; // USER_EXISTS, INVITED_PENDING_APPROVAL
	recommendationSources?: string[]; // Source of user invited (eg Google, Slack, Microsoft, Collab Graph?)
	sourceProduct: Product; // Product being initiated from (in case of server event with different )
	sourceTenantId: string; // TenantID of the product where the request was initiated
	targetProduct: Product; // Product being requested to
	targetTenantId: string; // TenantID of the product where the request is for
	touchpoint: EnablementTouchpoint; // Required attribute as source can't always be set in Jira
	traceId?: string; // Trace ID from the invite response
	userAaid: string; // AAID of user who the request is for
	userUuid?: string; // For 3P, if we don't have AAID yet: UUID of user who the request is for
};

type AnalyticsAttributes = TrackEventAttributes & Partial<OriginAttributes>;

// inviteStatus/userAaid aren't known for the "treated as succeeded despite a failure" branch
// (no specific status to report, and userAaid may be unresolved for email-only invites).
type InviteSucceededAttributes = Omit<AnalyticsAttributes, 'inviteStatus' | 'userAaid'> &
	Partial<Pick<AnalyticsAttributes, 'inviteStatus' | 'userAaid'>>;

type ErrorAnalyticsAttributes = {
	code: string; // Error code from request response
	message: string; // Error message from request response
	status?: number; // Numerical error status code from request response
};

function fireProductAccessRequested(
	attributes: AnalyticsAttributes,
	context: DependenciesContext<Deps>,
) {
	if (fg('expansion_services_invite_in_core_invites')) {
		fireProductAccessRequestedOnChannel(attributes, context);
		return;
	}
	context
		.dependencies()
		.createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				actionSubject: 'productAccess',
				action: 'requested',
				actionSubjectId: attributes.targetProduct,
				source: attributes.touchpoint,
				attributes,
				tags: ['growth'],
			} satisfies GasPurePayload,
		})
		.fire();
}

function fireProductAccessRequestedOnChannel(
	attributes: AnalyticsAttributes,
	context: DependenciesContext<Deps>,
) {
	const deps = context.dependencies();
	deps
		.createAnalyticsEvent({
			eventType: TRACK_EVENT_TYPE,
			actionSubject: 'productAccess',
			action: 'requested',
			actionSubjectId: attributes.targetProduct,
			source: attributes.touchpoint,
			attributes,
			tags: ['growth'],
		} satisfies GasCorePayload)
		.fire(deps.getAnalyticsChannel());
}

function fireUserInvited(attributes: AnalyticsAttributes, context: DependenciesContext<Deps>) {
	if (fg('expansion_services_invite_in_core_invites')) {
		fireUserInvitedOnChannel(attributes, context);
		return;
	}
	context
		.dependencies()
		.createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				actionSubject: 'user',
				action: 'invited',
				actionSubjectId: attributes.userAaid,
				source: attributes.touchpoint,
				attributes,
				tags: ['growth'],
			} satisfies GasPurePayload,
		})
		.fire();
}

function fireUserInvitedOnChannel(
	attributes: AnalyticsAttributes,
	context: DependenciesContext<Deps>,
) {
	const deps = context.dependencies();
	deps
		.createAnalyticsEvent({
			eventType: TRACK_EVENT_TYPE,
			actionSubject: 'user',
			action: 'invited',
			actionSubjectId: attributes.userAaid,
			source: attributes.touchpoint,
			attributes,
			tags: ['growth'],
		} satisfies GasCorePayload)
		.fire(deps.getAnalyticsChannel());
}

const inviteSucceededSLO = (
	attributes: InviteSucceededAttributes,
	context: DependenciesContext<Deps>,
) => {
	if (fg('expansion_services_invite_in_core_invites')) {
		inviteSucceededSLOOnChannel(attributes, context);
		return;
	}
	context
		.dependencies()
		.createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				actionSubject: 'sloInviteRequest',
				action: 'succeeded',
				source: attributes.touchpoint,
				attributes,
				tags: ['growth'],
			} satisfies GasPurePayload,
		})
		.fire();
};

const inviteSucceededSLOOnChannel = (
	attributes: InviteSucceededAttributes,
	context: DependenciesContext<Deps>,
) => {
	const deps = context.dependencies();
	deps
		.createAnalyticsEvent({
			eventType: OPERATIONAL_EVENT_TYPE,
			actionSubject: 'sloInviteRequest',
			action: 'succeeded',
			source: attributes.touchpoint,
			attributes,
			tags: ['growth'],
		} satisfies GasCorePayload)
		.fire(deps.getAnalyticsChannel());
};
